import { IconButton } from "@mui/material";
import React, { Fragment, useContext, useState } from "react";
import { FiBox, FiEdit, FiTrash } from "react-icons/fi";
import propTypes from "prop-types";

import { api, Package, ProductRate } from "../../../../logic/api";

import flex from "../../../../styles/flex";
import styles from "./styles";

import { calculatePackagePrice, PackageAvailability } from "../../../packages/builder/PackageBuilder";

import ErrorDialog from "../../../notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../notifications/progressDialog/progressDialog";
import ConfirmationDialog from "../../../notifications/confirmationDialog/ConfirmationDialog";

import { SessionContext } from "../../../../utility/useSession";
import { formatCurrency, isAdmin } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";
import { Trans, useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

const PackageListItem: React.FunctionComponent<{
  package: Package,
  onEditPackage: (pkg: Package) => void,

}> = (props) => {
  const [pkg] = useState(props.package);
  const session = useContext(SessionContext);
  const [showDeletePackage, setShowDeletePackage] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const { toFormattedDateString, localizeDate } = useDate();
  const requestDeletePackage = api.hooks.packages.delete();
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);

  const deletePackage = () => {
    if (pkg.id != null) {
      requestDeletePackage.send(session.current.context, {
        id: pkg.id
      }, (result) => {
        if (result != null) {
          setDeleted(true);
        }
      });
    }
  }

  if (deleted || session.current.site == null) {
    return <Fragment />
  }

  let availability: PackageAvailability = PackageAvailability.available;
  let iconColour = "black";

  const localizedStartOfDay = localizeDate(new Date())
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const localizedStartOfPackageStartDate = localizeDate(new Date(pkg.startDate))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  const packageStartsInTheFuture = Boolean(
    localizedStartOfPackageStartDate.diff(localizedStartOfDay).milliseconds > 0
  );
  if (packageStartsInTheFuture) {
    availability = PackageAvailability.NotYetAvailable;
    iconColour = "orange";
  }

  if (pkg.expires && pkg.endDate != null) {
    const localizedStartOfPackageEndDate = localizeDate(new Date(pkg.endDate))
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

    const packagedHasExpired = Boolean(
      localizedStartOfPackageEndDate.diff(localizedStartOfDay).milliseconds < 0
    );
    if (packagedHasExpired) {
      iconColour = "red";
      availability = PackageAvailability.NoLongerAvailable;
    }
  }

  const startDate = toFormattedDateString(new Date(pkg.startDate));
  const endDate = pkg.endDate ? toFormattedDateString(new Date(pkg.endDate)) : '';
  let availabilityText = '';

  if (availability === PackageAvailability.NotYetAvailable) {
    availabilityText = '[Not yet available]';
  } else if (availability === PackageAvailability.NoLongerAvailable) {
    availabilityText = '[No longer available]';
  }

  return (
    <Fragment>
      <ErrorDialog err={requestDeletePackage.err} />
      <ProgressDialog status={requestDeletePackage.status} />

      <ConfirmationDialog
        title={t("overview.confirmDelete")}
        message={t("lists.confirmPkgDelete", { name: pkg.name })}
        show={showDeletePackage}
        onClose={(confirm) => {
          setShowDeletePackage(false);
          if (confirm == true) {
            deletePackage();
          }
        }}
      />

      <div style={{ ...styles.listItem, opacity: pkg.active == true ? 1 : 0.5 }}>
        <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>

          <div style={flex.flexAuto}>
            <div>
              <IconButton disabled={true}>
                <FiBox color={iconColour} />
              </IconButton>
            </div>
          </div>

          <div style={{ ...flex.flexFill, textAlign: "left" }}>
            <div>
              <b>{pkg.name}</b>
            </div>

            {(availability != PackageAvailability.available) && (
              <div>
                <small>
                  <Trans
                    i18nKey="lists.packageAvailability"
                    values={{
                      startDate: `<b>${startDate}</b>`,
                      endDate: endDate ? ` - <b>${endDate}</b>` : '',
                      availability: availabilityText ? `<b><i>${availabilityText}</i></b>` : ''
                    }}
                    components={{
                      b: <b />,
                      i: <i style={{ color: `${availability === PackageAvailability.NotYetAvailable ? 'orange' : 'red'}` }} />,
                    }}
                  />
                </small>
              </div>
            )}

            {pkg.walkinRateType == ProductRate.adjusted && (
              <small>
                <Trans
                  i18nKey="lists.walkInRate"
                  values={{
                    rate: formatCurrency(pkg.walkinRate, session)
                  }}
                  components={{
                    0: <b />,
                    1: <b style={{ color: "red" }} />
                  }}
                />
              </small>
            )}


            <div>
              <small style={{ color: "orange" }}>
                <b>{pkg.code}</b>
                {(pkg.ref != null && pkg.ref.length > 0) && (
                  <span> / <b>{pkg.ref}</b></span>
                )}
              </small>
            </div>

          </div>

          <div style={flex.flexAuto}>
            <div style={{ marginRight: 50 }}>
              <b>{formatCurrency(calculatePackagePrice(pkg.products).dealPriceIncVat, session)}</b> <small>{t("lists.incTax")}</small>
            </div>
          </div>


          {/*<div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>isAdmin(session)  && (
                          <div>
                              <IconButton onClick={() => {
                                  // props.onEditUser(product);
                                  history.push(paths.workflow(pkg.code))
                              }}>
                                  <FiGitCommit></FiGitCommit>
                              </IconButton>
                              <div><small>Workflow</small></div>
                          </div>
                          )</div>*/}


          <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
            {(isRbacReleased ? hasPermission("bookingManagement.saleItems.update") : isAdmin(session)) && (
              <div>
                <IconButton
                  onClick={() => {
                    props.onEditPackage(props.package);
                  }}
                >
                  <FiEdit />
                </IconButton>
                <div><small>{t("general.edit")}</small></div>
              </div>
            )}
          </div>



          {(isRbacReleased ? hasPermission("bookingManagement.saleItems.delete") : isAdmin(session)) && (
            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              <IconButton
                onClick={() => {
                  setShowDeletePackage(true);
                }}
              >
                <FiTrash />
              </IconButton>
              <div><small>{t("general.delete")}</small></div>
            </div>
          )}



        </div>
      </div >
    </Fragment>
  );
}

PackageListItem.propTypes = {
  package: propTypes.any.isRequired,
  onEditPackage: propTypes.func.isRequired
}

export default PackageListItem;
